import React, { useCallback, useEffect, useState } from "react";
import { Button, TextField } from "@material-ui/core";
import { adsManagerClaimSettings } from "../../modules/snapshotEditorApi";
import { ERROR_TEXT, FieldRow, OUTLINED_BUTTON_STYLE, PanelHint, SectionHeader } from "../pe3d/pe3dPanelKit";

// Older agent runtimes may still report a stale task's 403 as lastError.
// Webhook health is handled by the runtime and recorded in server logs.
function visibleBackgroundError(status) {
  const message = status?.lastError;
  if (message && !/^Asana \/tasks\/\d+\?opt_fields=[^\n]* responded (403|404|410):/.test(message)) return message;
  return null;
}

const problemStyle = { fontSize: 12, lineHeight: 1.5, color: ERROR_TEXT, padding: "4px 6px" };

/** The daily claim limits, a section of the Queue tab's limits. */
export default function AdsManagerClaims({ baseID, isActive, canEdit }) {
  const [status, setStatus] = useState(null);
  const [limits, setLimits] = useState({ sip: "0", mip: "0", tolerance: "0" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const accept = useCallback((result, updateLimits) => {
    if (!result.ok) throw new Error(result.error?.message || result.error?.error || "Could not load claim settings.");
    setStatus(result.data);
    if (updateLimits) {
      const { sip, mip, tolerance = 0 } = result.data.settings;
      setLimits({ sip: String(sip), mip: String(mip), tolerance: String(tolerance) });
    }
    setError("");
  }, []);

  useEffect(() => {
    if (!isActive) return undefined;
    let disposed = false;
    const load = async () => {
      try {
        const result = await adsManagerClaimSettings(baseID);
        if (!disposed) accept(result, !dirty);
      } catch (err) { if (!disposed) setError(err.message); }
    };
    void load();
    const timer = setInterval(load, 30000);
    return () => { disposed = true; clearInterval(timer); };
  }, [baseID, isActive, dirty, accept]);

  const valid = ["sip", "mip", "tolerance"].every((key) => /^\d+$/.test(limits[key]) && Number.isSafeInteger(Number(limits[key])));
  const save = async () => {
    setSaving(true);
    try {
      accept(await adsManagerClaimSettings(baseID, { sip: Number(limits.sip), mip: Number(limits.mip), tolerance: Number(limits.tolerance) }), true);
      setDirty(false);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const field = (key, label, ariaLabel, hint) => (
    <div key={key}>
      <FieldRow label={label}>
        <TextField fullWidth type="number" value={limits[key]} disabled={!canEdit || !status || saving}
          inputProps={{ min: 0, step: 1, "aria-label": ariaLabel }}
          onChange={(event) => { setLimits({ ...limits, [key]: event.target.value }); setDirty(true); }} />
      </FieldRow>
      <PanelHint>{hint}</PanelHint>
    </div>
  );
  const claimed = (key) => (status ? `${status.counts[key]} assets claimed this period${status.reserved?.[key] ? ` · ${status.reserved[key]} pending` : ""}` : "Loading…");

  return (
    <div>
      <SectionHeader title="Task claiming" />
      <PanelHint>
        Daily limits count assets, not tasks: a task asks for one asset per delivery name in its description.
        Tasks are chosen so their total lands closest to the limit, oldest first, and never more than the tolerance above it.
        Limits reset at 14:00 Türkiye time. Completed tasks still count. Tasks assigned to us by others do not use this quota.
      </PanelHint>
      {field("sip", "SIP per day", "SIP daily limit (assets)", claimed("sip"))}
      {field("mip", "MIP per day", "MIP daily limit (assets)", claimed("mip"))}
      {field("tolerance", "Tolerance", "Tolerance", "How many assets a day may go above each limit to take a task that would otherwise not fit. 0 never goes above.")}
      {status?.unreadable?.length > 0 && (
        <div style={problemStyle}>{`Not claimed — asset count could not be read from the description: ${status.unreadable.map((task) => task.name).join(", ")}`}</div>
      )}
      {status && !status.enabled && <PanelHint>Automatic claiming is not enabled on the server.</PanelHint>}
      {visibleBackgroundError(status) && <div style={problemStyle}>{visibleBackgroundError(status)}</div>}
      {error && <div style={problemStyle} role="alert">{error}</div>}
      {dirty && (
        <div style={{ padding: "4px 6px 8px" }}>
          <Button size="small" style={{ ...OUTLINED_BUTTON_STYLE, fontSize: 12, padding: "2px 8px", minWidth: 0 }} disabled={!canEdit || !status || !valid || saving} onClick={save}>
            {saving ? "Saving…" : "Save limits"}
          </Button>
        </div>
      )}
    </div>
  );
}
